import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import Switch from "../../components/Switch";

export default function AdminAutomationRules() {
  const [settings, setSettings] = useState(null);
  const [billing, setBilling] = useState({ auto_invoice_issued: true, auto_invoice_pending: true, auto_invoice_paid: true, invoice_visible_on_due: true });

  useEffect(() => {
    api.get("/admin/automation-settings").then((r) => setSettings(r.data));
    api.get("/admin/settings/billing").then((r) => setBilling(r.data));
  }, []);

  const toggle = async (key) => {
    const next = { ...settings, [key]: !settings[key] };
    setSettings(next);
    try { await api.put("/admin/automation-settings", next); toast.success("Règle mise à jour"); }
    catch { toast.error("Erreur"); setSettings(settings); }
  };

  const toggleBilling = async (key) => {
    const next = { ...billing, [key]: !billing[key] };
    setBilling(next);
    try { await api.put("/admin/settings/billing", { [key]: next[key] }); toast.success(next[key] ? "Règle activée" : "Règle désactivée"); }
    catch { setBilling(billing); toast.error("Échec de l'enregistrement"); }
  };

  if (!settings) return null;

  const Rule = ({ rkey, title, desc }) => (
    <div className="bg-sable rounded-sm border border-marine/10 p-5 flex items-start justify-between gap-4" data-testid={`rule-${rkey}`}>
      <div className="flex-1">
        <div className="font-medium text-marine">{title}</div>
        <p className="text-sm text-marine/80 mt-1">{desc}</p>
      </div>
      <Switch checked={!!settings[rkey]} onChange={() => toggle(rkey)} testid={`rule-toggle-${rkey}`} title={title} className="mt-1" />
    </div>
  );

  return (
    <div className="max-w-3xl">
      <PageTitle overline="Automatisations" title="Règles" />
      <p className="text-sm text-marine/80 mb-8">Activez ou désactivez les comportements automatiques de la plateforme.</p>

      <section className="mb-10" data-testid="rules-section-missions">
        <div className="flex items-center gap-2 mb-3">
          <Icons.Clapperboard size={16} className="text-marine" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-marine">Missions</h2>
        </div>
        <div className="space-y-3">
          <Rule rkey="mission_auto_advance" title="Activation automatique de la phase suivante"
            desc="Lorsqu'une phase de mission est validée (par vous ou le collaborateur), la phase suivante encore « à venir » démarre automatiquement. Désactivez pour ouvrir les phases vous-même, depuis le menu de statut de la fiche mission." />
        </div>
      </section>

      <section data-testid="rules-section-billing">
        <div className="flex items-center gap-2 mb-3">
          <Icons.ReceiptText size={16} className="text-bordeaux" />
          <h2 className="text-sm font-semibold uppercase tracking-wider text-bordeaux">Facturation</h2>
        </div>
        <div className="bg-bordeaux-50 rounded-sm border border-bordeaux/20 p-4" data-testid="billing-settings">
          <p className="text-xs text-marine/80 mb-3 leading-relaxed">Ces interrupteurs changent automatiquement le <strong>statut des factures</strong>. Ce ne sont pas des tâches/notifications.</p>
          <div className="divide-y divide-bordeaux/10 bg-sable rounded-sm border border-bordeaux/10 px-4">
            {[
              { key: "auto_invoice_issued", label: "Marquer la facture « Émise »", desc: "Dès qu'un fichier de facture est joint à l'échéance, son statut passe à « Émise »." },
              { key: "auto_invoice_pending", label: "Passer la facture en « En attente »", desc: "Dès que la date d'échéance est atteinte (et que la facture n'est pas réglée), son statut passe à « En attente » de paiement." },
              { key: "auto_invoice_paid", label: "Passer la facture en « Payée »", desc: "Lorsqu'une échéance est intégralement réglée, son statut passe automatiquement à « Payée »." },
              { key: "invoice_visible_on_due", label: "Publier la facture au client à l'échéance", desc: "Le fichier de facture (PDF) devient visible par l'entreprise sur son portail à partir de la date d'échéance, et reste disponible ensuite. Désactivé : la facture est visible dès son dépôt." },
            ].map((r) => (
              <div key={r.key} className="flex items-start justify-between gap-6 py-3">
                <div>
                  <div className="font-medium text-marine flex items-center gap-2"><Icons.ReceiptText size={16} className="text-bordeaux" /> {r.label}</div>
                  <p className="text-sm text-marine/80 mt-1 leading-relaxed max-w-2xl">{r.desc}</p>
                </div>
                <Switch checked={!!billing[r.key]} onChange={() => toggleBilling(r.key)} testid={`toggle-${r.key.replace(/_/g, "-")}`} title={r.label} className="mt-1" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
